import React from 'react';
import { Card } from 'primereact/card';

type Translator = (key: string) => string;

export interface JsonHeaderData {
  // Explorer
  ceb_version?: string | null;
  esp_version?: string | null;
  epos_version?: string | null;
  battery_cycle?: number | string | null;

  // Atlas
  mpb_version?: string | null;
  scb_version?: string | null;
  mcb_version?: string | null;
  total_device_steps?: number | null;
  total_device_time?: number | null; // segundos, ms… (según tu backend)

  // Comunes
  app_version?: string | null;
  structure_version?: string | null;

  // Permite crecer sin romper tipos
  [k: string]: unknown;
}

interface DeviceVersionCardProps {
  jsonHeaderData: JsonHeaderData;
  serialNumber: string;
  t: Translator;
}

const FIELD_CONFIG: Array<{ key: keyof JsonHeaderData; i18n: string }> = [
  // Explorer
  { key: 'ceb_version', i18n: 'pages.devices.ceb_version' },
  { key: 'esp_version', i18n: 'pages.devices.esp_version' },
  { key: 'epos_version', i18n: 'pages.devices.epos_version' },
  { key: 'battery_cycle', i18n: 'pages.devices.battery_cycles' },

  // Atlas
  { key: 'mpb_version', i18n: 'pages.devices.mpb_version' },
  { key: 'scb_version', i18n: 'pages.devices.scb_version' },
  { key: 'mcb_version', i18n: 'pages.devices.mcb_version' },
  { key: 'total_device_steps', i18n: 'pages.devices.total_device_steps' },
  { key: 'total_device_time', i18n: 'pages.devices.total_device_time' },

  // Comunes
  { key: 'app_version', i18n: 'pages.devices.app_version' },
  { key: 'structure_version', i18n: 'pages.devices.structure_version' },
];

const DeviceVersionCard: React.FC<DeviceVersionCardProps> = ({
  jsonHeaderData,
  serialNumber,
  t,
}) => {
  // construye las filas solo con las claves presentes (permitiendo 0)
  const rows = FIELD_CONFIG.map(({ key, i18n }) => {
    const value = jsonHeaderData[key];
    return value !== undefined && value !== null
      ? { label: t(i18n), value: String(value) }
      : null;
  }).filter((x): x is { label: string; value: string } => x !== null);

  const hasAnyData = Boolean(serialNumber) || rows.length > 0;

  return (
    <div id="version" className="w-full mt-6 mb-8">
      <Card className="w-full max-w-3xl mx-auto rounded-2xl pl-4 shadow-3 surface-card">
        <h2 className="text-xl sm:text-2xl font-bold px-5 mb-5 border-b pb-2">
          <i className="pi pi-cog mr-2" />
          {t('pages.devices.softwareVersion')}
        </h2>

        {hasAnyData ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 px-5 mb-5 gap-4 text-sm sm:text-base">
            {/* Serial si lo tienes */}
            {serialNumber && (
              <div className="flex flex-col items-center text-center">
                <span className="font-semibold mr-2">
                  {t('pages.devices.num_serial')}:
                </span>
                <span>{serialNumber}</span>
              </div>
            )}

            {/* Campos dinámicos presentes */}
            {rows.length > 0 ? (
              rows.map(({ label, value }) => (
                <div
                  key={`${label}-${value}`}
                  className="flex flex-col items-center text-center"
                >
                  <span className="font-semibold mr-2">{label}:</span>
                  <span>{value}</span>
                </div>
              ))
            ) : (
              // Si solo hay serial pero no campos
              <div className="col-span-2 text-center italic text-600">
                {t('emptyMessage')}
              </div>
            )}
          </div>
        ) : (
          <div className="col-span-2 text-left italic text-600 px-5">
            {t('emptyMessage')}
          </div>
        )}
      </Card>
    </div>
  );
};

export default DeviceVersionCard;
